<template>
  <SubHeading :home="home" :tabs="tabs" :currTab="currTab" @active="handleActive" />

  <!-- breadcrumbs -->
  <nav class="sba-collection-breadcrumb z-depth-0">
    <div class="container nav-wrapper">
      <div class="sba-collection-breadcrumb-row">
        <router-link to="/" class="breadcrumb sba-collection-crumb teal-text">
          <i class="material-icons">home</i>
          <span>Dashboard</span>
        </router-link>
        <i class="material-icons sba-collection-crumb-separator">chevron_right</i>
        <router-link to="/provider" class="breadcrumb sba-collection-crumb blue-grey-text text-darken-3">
          <span>Providers Home</span>
        </router-link>
        <i class="material-icons sba-collection-crumb-separator">chevron_right</i>
        <span class="breadcrumb sba-collection-crumb blue-grey-text pointable">
          <span>View</span>
        </span>
        <span class="sba-collection-context">Provider Workspace</span>
      </div>
    </div>
  </nav>

  <div id="top"></div>

  <div class="sba-collection-page-surface is-workbench provider-secondary-surface">
    <div class="container">
      <LoadingDiv v-if="!isReady" />

      <div class="provider-workbench sba-slide-in" v-else>
        <section class="provider-secondary-header">
          <section class="provider-super-card">
            <button type="button" class="secondary-page-back" title="Back to All Providers"
              @click.prevent="handleActive(home)">
              <i class="material-icons">keyboard_return</i>
            </button>
            <div class="provider-super-copy">
              <button type="button" class="secondary-parent-link" @click.prevent="handleActive(home)">
                All Providers
              </button>
              <small>Back to provider list</small>
            </div>
          </section>

          <section class="provider-dossier-card">
            <div class="provider-dossier-title">
              <label class="active teal-text">Provider Workspace</label>
              <h5>{{ capWords(provider?.name || "Provider") }}</h5>
              <p>
                Manage this provider account, tariff records, and access settings.
              </p>
            </div>

            <div class="provider-dossier-context">
              <span class="context-pill" :class="providerStatusClass">{{ providerStatusLabel }}</span>
              <span class="context-pill">{{ provider?.state || "State not recorded" }}</span>
              <span class="context-pill">{{ provider?.phone || "No phone recorded" }}</span>
              <span class="context-pill is-sync" v-if="!siloMode">
                {{ provider?.opid ? "oneProvider synced" : "Sync pending" }}
              </span>
            </div>
          </section>

          <section class="provider-mode-strip" :class="{ 'has-submode': currTabSmall?.includes('Tariff') }">
            <button type="button" class="provider-home-icon-action" title="Back to All Providers" @click.prevent="handleActive(home)">
              <i class="material-icons">close</i>
            </button>
            <div class="provider-mode-actions">
              <button v-for="tab in tabsMenu" :key="tab.id" type="button" class="provider-mode-option"
                :class="{ 'is-active': currTabMenu?.title === tab.title }" @click.prevent="handleActiveMenu(tab)">
                {{ tab.title }}
              </button>
            </div>
          </section>

          <section class="provider-submode-strip" v-if="currTabSmall?.includes('Tariff')">
            <div class="provider-submode-copy">
              <span>{{ subModeTitle }}</span>
              <small>{{ subModeDescription }}</small>
            </div>
            <div class="provider-submode-actions">
              <button v-for="tab in tariffTabs" :key="tab.id" type="button" class="provider-submode-option"
                :class="{ 'is-active': currTabSmall === tab.title }" @click.prevent="handleActiveMenu(tab)">
                {{ tab.title.split(" -> ")[1] }}
              </button>
            </div>
          </section>
        </section>

        <section
          class="provider-secondary-body-frame"
          :class="{ 'is-tariff-section': currTabSmall?.includes('Tariff') }"
        >
          <LoadingDiv v-if="!currTabSmall" />

          <!-- Profile -->
          <ProfileDiv :provider="provider" @update="handleUpdate" v-if="currTabSmall?.includes('Profile')" />
          <!-- Tariff View -->
          <TariffDiv :provider="provider" startTab="view" @reload="handleReload"
            v-if="currTabSmall?.includes('Tariff') && currTabSmall?.includes('View')" />
          <!-- Tariff Add -->
          <TariffDiv :provider="provider" startTab="add" @reload="handleReload"
            v-if="currTabSmall?.includes('Tariff') && currTabSmall?.includes('Add')" />
          <!-- Tariff Replace -->
          <TariffDiv :provider="provider" startTab="replace" @reload="handleReload"
            v-if="currTabSmall?.includes('Tariff') && currTabSmall?.includes('Replace')" />
          <!-- Tariff Band -->
          <TariffDiv :provider="provider" startTab="band" @reload="handleReload"
            v-if="currTabSmall?.includes('Tariff') && currTabSmall?.includes('Band')" />
          <!-- Tariff AI Marker -->
          <TariffDiv :provider="provider" startTab="ai" @reload="handleReload"
            v-if="currTabSmall?.includes('Tariff') && currTabSmall?.includes('AI Marker')" />
          <!-- Settings -->
          <SettingsDiv :easyHMOProvider="provider" @reload="handleReload" v-if="currTabSmall?.includes('Settings')" />
        </section>
      </div>
    </div>
  </div>
</template>


<script>
import { computed, onMounted, onUpdated, ref, watchEffect } from "vue";
import useModuleNavigation from "@/composables/useModuleNavigation";
import getItem from "@/composables/getItem";
import SubHeading from "@/components/HeaderSubDiv.vue";
import LoadingDiv from "@/components/LoadingDiv.vue";
import ProfileDiv from "@/components/provider/ProfileProviderComponent.vue";
import TariffDiv from "@/components/provider/TariffProviderComponent.vue";
import SettingsDiv from "@/components/provider/SettingsProviderComponent.vue";
import {
  capWordsFx,
} from "@/composables/fxLibrary/coreFx.js";
import { siloMode } from '@/laravel/config';
import M from "materialize-css";


export default {
  name: "providerView",
  components: { SubHeading, LoadingDiv, ProfileDiv, SettingsDiv, TariffDiv },
  props: ["id"],
  setup(props) {
    onMounted(() => {
      // page name
      document.title = "View Single Provider | EasyHMO";
    });

    const response = ref(null);
    const isReady = ref(false);
    const error = ref(null);
    const currTabMenu = ref(null);
    const tabsMenu = ref([]);
    const currTabSmall = ref(null);
    const tabsSmall = ref([]);
    const providerId = ref(props.id);
    const home = ref(null);
    const currTab = ref(null);
    const tabs = ref([]);

    const { routeToTab } = useModuleNavigation();

    // dropdown
    onMounted(() => {
      const elems = document.querySelectorAll(".dropdown-trigger");
      M.Dropdown.init(elems, {});
    });

    onUpdated(() => {
      const elems = document.querySelectorAll(".dropdown-trigger");
      M.Dropdown.init(elems, {});
    });

    // menu
    onMounted(() => {
      // load tab
      tabs.value.push({ id: 1, name: "Providers", title: "All", icon: "arrow_back", url: "provider", hash: null, });
      tabs.value.push({ id: 2, name: "Providers", title: "View", icon: null, url: null, hash: null, });
      tabs.value.push({ id: 3, name: "Providers", title: "Add Provider", icon: "add_circle", url: "providerAdd", hash: null, });
      // check silo
      if (!siloMode) {
       tabs.value.push({ id: 4, name: "Providers", title: "Sync", icon: "sync", url: "providerSync", hash: null, });
      }

      // set default
      currTab.value = tabs.value.find(x => x.title.includes("View"));
      // set home
      home.value = tabs.value.find(x => x.id === 1);
    });

    // handle curr status
    const handleActive = (tab) => {
      routeToTab(tab, { replace: !tab?.url });
    };

    //from functions
    const capWords = (val) => {
      return capWordsFx(val);
    };

    // UI modification
    const maxRule = (text) => {
      let maxLength = 15;
      if (text.length > maxLength) {
        return ` ${text.substring(0, maxLength)} ... `;
      } else {
        return ` ${text} `;
      }
    };

    onMounted(() => {
      // load tab
      tabsSmall.value.push({ id: tabsSmall.value.length + 1, title: "Profile -> Review Profile" });
      tabsSmall.value.push({ id: tabsSmall.value.length + 1, title: "Spacer" });
      tabsSmall.value.push({ id: tabsSmall.value.length + 1, title: "Tariff -> View All" });
      tabsSmall.value.push({ id: tabsSmall.value.length + 1, title: "Tariff -> Add Item" });
      tabsSmall.value.push({ id: tabsSmall.value.length + 1, title: "Tariff -> Replace All" });
      tabsSmall.value.push({ id: tabsSmall.value.length + 1, title: "Tariff -> Tariff Band" });
      tabsSmall.value.push({ id: tabsSmall.value.length + 1, title: "Tariff -> AI Marker" });
      tabsSmall.value.push({ id: tabsSmall.value.length + 1, title: "Spacer" });
      tabsSmall.value.push({ id: tabsSmall.value.length + 1, title: "Settings -> Manage Settings" });
    });

    // Initiate
    onMounted(() => {
      // load tab
      // tabsMenu.value.push({ id: tabsMenu.value.length + 1, title: "Menu", dropdown: false });
      tabsMenu.value.push({ id: tabsMenu.value.length + 1, title: "Profile", dropdown: false });
      tabsMenu.value.push({ id: tabsMenu.value.length + 1, title: "Tariff", dropdown: true });
      tabsMenu.value.push({ id: tabsMenu.value.length + 1, title: "Settings", dropdown: false });
      // set default
      currTabMenu.value = tabsMenu.value[0];
    });

    // handle curr status
    const handleActiveSmall = (tab) => {
      // update show and components
      currTabSmall.value = tab;
      // highlight menu
      if (currTabSmall.value) {
        currTabMenu.value = tabsMenu.value.find(x => currTabSmall.value.toLowerCase().includes(x.title.toLowerCase()));
      } else {
        currTabMenu.value = tabsMenu.value[0];
      }
    };

    // handle curr status
    const handleActiveMenu = (tab) => {

      // check non-drop-downs
      let newActive = tabsSmall.value.find(x => x.title.toLowerCase().includes(tab.title.toLowerCase()));
      if (newActive) {
        handleActiveSmall(newActive?.title);
      }else{
        handleActiveSmall();
      }

      // check non-drop-downs
      // if (!tab?.dropdown) {
      //   let newActive = tabsSmall.value.find(x => x.title.toLowerCase().includes(tab.title.toLowerCase()));
      //   if (newActive) {
      //     handleActiveSmall(newActive?.title);
      //   } else {
      //     handleActiveSmall();
      //   }
      // } else {
      //   handleActiveSmall();
      // }
    };

    // Title
    watchEffect(() => {
      if (response.value) {
        // load title
        setTimeout(() => {
          var elems = document.querySelectorAll(".tooltipped");
          M.Tooltip.init(elems, {});
        }, 2000);
      }
    });

    // get provider
    const getProvider = () => {
      (async () => {
        const { res, err } = await getItem("provider", props.id);

        if (res.value) {
          response.value = res.value.data;
          setDefaultSmallTab();
        } else {
          error.value = err.value;
        }

        // page is ready
        isReady.value = true;
      })();
    }

    // Initiate
    onMounted(() => {
      // start
      getProvider();
    });

    // handle emitted update
    const handleUpdate = (val) => {
      response.value = val;
    };

    // handle reload
    const handleReload = (val) => {
      if (val) {
        // restart
        getProvider();
      }
    };

    // output
    const provider = computed(() => {
      if (!response.value) {
        return [];
      } else {
        return response.value;
      }
    });

    const tariffTabs = computed(() => {
      return tabsSmall.value.filter(x => x.title.includes("Tariff"));
    });

    const setDefaultSmallTab = () => {
      if (currTabSmall.value) return;

      handleActiveSmall(provider.value?.status === "s" ? "Profile -> Review Profile" : "Tariff -> View All");
    };

    const subModeTitle = computed(() => {
      if (!currTabSmall.value?.includes("->")) {
        return "Tariff";
      }

      return currTabSmall.value.replace(" -> ", " ");
    });

    const subModeDescription = computed(() => {
      const descriptions = {
        "Tariff -> View All": "Review provider tariff items and current prices.",
        "Tariff -> Add Item": "Add a new tariff item for this provider.",
        "Tariff -> Replace All": "Replace this provider's tariff records in bulk.",
        "Tariff -> Tariff Band": "Manage tariff band rules for this provider.",
        "Tariff -> AI Marker": "Mark provider tariff items that can support auto-vetting.",
      };

      return descriptions[currTabSmall.value] || "Select the tariff task.";
    });

    const providerStatusLabel = computed(() => {
      if (provider.value?.status === "s") {
        return "Inactive provider";
      }

      if (provider.value?.status === "p") {
        return "Pending approval";
      }

      return "Active provider";
    });

    const providerStatusClass = computed(() => {
      if (provider.value?.status === "s") {
        return "is-inactive";
      }

      if (provider.value?.status === "p") {
        return "is-pending";
      }

      return "is-active";
    });

    return {
      handleActive,
      home,
      currTab,
      tabs,
      handleActiveSmall,
      handleActiveMenu,
      handleReload,
      handleUpdate,
      capWords,
      maxRule,
      isReady,
      tabsSmall,
      currTabMenu,
      tabsMenu,
      currTabSmall,
      error,
      providerId,
      provider,
      providerStatusClass,
      providerStatusLabel,
      siloMode,
      tariffTabs,
      subModeTitle,
      subModeDescription,
    };
  },
};
</script>

<style scoped>
.provider-secondary-surface {
  min-height: calc(100vh - 260px);
  padding-bottom: 56px;
}

.provider-workbench {
  color: #263238;
  padding-bottom: 48px;
  padding-top: 0;
  position: relative;
  width: 100%;
  z-index: 1;
}

.provider-super-card,
.provider-dossier-card,
.provider-mode-strip,
.provider-submode-strip,
.provider-secondary-body-frame,
.provider-workbench :deep(.card),
.provider-workbench :deep(.card-panel),
.provider-workbench :deep(.provider-tariff-card),
.provider-workbench :deep(.provider-tariff-search-card),
.provider-workbench :deep(.provider-tariff-row),
.provider-workbench :deep(.provider-tariff-empty),
.provider-workbench :deep(.profile-card),
.provider-workbench :deep(.settings-summary),
.provider-workbench :deep(.setting-card) {
  animation: sbaSlideIn var(--sba-slide-duration, 260ms) ease-out both;
}

.provider-secondary-header {
  box-shadow: 0 20px 34px -32px rgba(38, 50, 56, 0.82);
  margin-bottom: 0;
  overflow: visible;
  position: relative;
}

.provider-super-card {
  align-items: center;
  background: #fff8e1;
  border: 1px solid #c4d3d9;
  border-bottom: 0;
  border-radius: 6px 6px 0 0;
  box-shadow: 0 10px 20px -24px rgba(121, 85, 72, 0.55);
  display: flex;
  gap: 10px;
  min-height: 46px;
  padding: 10px 14px;
  position: relative;
  width: 100%;
  z-index: 3;
}

.provider-super-copy {
  display: flex;
  flex-direction: column;
  gap: 2px;
  line-height: 1.15;
}

.provider-dossier-card {
  align-items: flex-end;
  background: #ffffff;
  border: 1px solid #c4d3d9;
  border-radius: 0;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  box-shadow: 0 12px 20px -18px rgba(38, 50, 56, 0.55);
  display: flex;
  gap: 22px;
  justify-content: space-between;
  padding: 26px 34px;
  position: relative;
}

.provider-dossier-title {
  min-width: 0;
}

.secondary-page-back {
  align-items: center;
  background: #00897b;
  border: 1px solid #00796b;
  border-radius: 5px;
  color: #ffffff;
  cursor: pointer;
  display: inline-flex;
  flex: 0 0 auto;
  height: 24px;
  justify-content: center;
  margin: 0;
  padding: 0;
  width: 24px;
}

.secondary-page-back:hover {
  background: #00695c;
  border-color: #00695c;
}

.secondary-page-back i {
  color: #ffffff;
  font-size: 16px;
  line-height: 16px;
}

.secondary-parent-link {
  background: transparent;
  border: none;
  border-radius: 4px;
  color: #4e342e;
  cursor: pointer;
  font: inherit;
  font-size: 0.78rem;
  font-weight: 800;
  margin: 0;
  padding: 0;
  text-align: left;
  text-transform: uppercase;
  transition: background-color 0.18s ease, color 0.18s ease;
}

.secondary-parent-link:hover {
  color: #00897b;
}

.provider-super-copy small {
  color: #6d4c41;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: none;
}

.provider-dossier-title label {
  display: block;
  font-size: 0.82rem;
  font-weight: 600;
  margin-bottom: 8px;
}

.provider-dossier-title h5 {
  color: #263238;
  font-weight: 700;
  margin: 4px 0 0;
  overflow-wrap: anywhere;
}

.provider-dossier-title p {
  color: #607d8b;
  line-height: 1.55;
  margin: 0;
  max-width: 760px;
}

.provider-dossier-context {
  align-items: flex-end;
  display: flex;
  flex: 0 0 auto;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
  max-width: 430px;
}

.context-pill {
  background: #fafafa;
  border: 1px solid #e1e8eb;
  border-radius: 999px;
  color: #455a64;
  display: inline-flex;
  font-size: 0.78rem;
  font-weight: 700;
  line-height: 1.2;
  padding: 8px 11px;
}

.context-pill.is-active {
  background: #e8f3f2;
  border-color: #9bd2cb;
  color: #00695c;
}

.context-pill.is-inactive {
  background: #ffebee;
  border-color: #ffcdd2;
  color: #b71c1c;
}

.context-pill.is-pending {
  background: #fff8e1;
  border-color: #ffe082;
  color: #795548;
}

.context-pill.is-sync {
  background: #eef5fb;
  border-color: #d7e6f1;
  color: #285a82;
}

.provider-mode-strip,
.provider-submode-strip {
  align-items: center;
  border: 1px solid #c4d3d9;
  border-top: 0;
  display: flex;
  gap: 18px;
  justify-content: space-between;
}

.provider-mode-strip {
  background: #fbfcfd;
  border-radius: 0 0 6px 6px;
  box-shadow: none;
  color: #263238;
  min-height: 76px;
  padding: 16px 20px;
}

.provider-mode-strip.has-submode {
  border-radius: 0;
}

.provider-submode-strip {
  background: #ffffff;
  border-top: 1px solid #e1e8eb;
  border-radius: 0 0 6px 6px;
  min-height: 78px;
  padding: 16px 20px;
}

.provider-submode-copy {
  display: grid;
  gap: 3px;
}

.provider-submode-copy span {
  font-size: 0.76rem;
  font-weight: 800;
  text-transform: uppercase;
}

.provider-submode-copy small {
  color: #607d8b;
  font-weight: 700;
}

.provider-home-icon-action {
  align-items: center;
  background: #f8fafb;
  border: 1px solid #90a4ae;
  border-radius: 50%;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.92);
  color: #263238;
  cursor: pointer;
  display: inline-flex;
  flex: 0 0 auto;
  height: 34px;
  justify-content: center;
  transition: background-color 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease, color 0.18s ease, transform 0.18s ease;
  width: 34px;
}

.provider-home-icon-action:hover {
  background: #e0f2f1;
  border-color: #00897b;
  box-shadow: 0 8px 18px -16px rgba(0, 105, 92, 0.72);
  color: #00695c;
  transform: translateY(-1px);
}

.provider-home-icon-action i {
  font-size: 18px;
  line-height: 18px;
}

.provider-mode-actions,
.provider-submode-actions {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}

.provider-mode-actions {
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid rgba(183, 197, 202, 0.72);
  border-radius: 6px;
  gap: 4px;
  padding: 5px;
}

.provider-mode-option,
.provider-submode-option {
  border-radius: 6px;
  cursor: pointer;
  font-weight: 800;
  min-height: 38px;
  padding: 0 15px;
}

.provider-mode-option {
  background: transparent;
  border: 1px solid transparent;
  color: #263238;
  transition: background-color 0.18s ease, border-color 0.18s ease, color 0.18s ease, transform 0.18s ease;
}

.provider-mode-option:hover {
  background: #eef3f6;
  border-color: #d7e1e5;
}

.provider-mode-option.is-active {
  background: #263238;
  border-color: #263238;
  box-shadow: 0 6px 14px -10px rgba(38, 50, 56, 0.75);
  color: #ffffff;
}

.provider-submode-option {
  align-items: center;
  background: #ffffff;
  border: 1px solid #c4d3d9;
  color: #455a64;
  display: inline-flex;
  font-size: 0.8rem;
  gap: 8px;
  letter-spacing: 0;
  min-height: 42px;
  transition: background-color 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease, color 0.18s ease;
}

.provider-submode-option:hover {
  background: #eef7f7;
  border-color: #80cbc4;
  box-shadow: 0 10px 18px -18px rgba(0, 105, 92, 0.55);
  color: #00695c;
}

.provider-submode-option.is-active {
  border-color: #00897b;
  color: #00695c;
}

.provider-secondary-body-frame {
  background: #f5f7f8;
  border: 1px solid #c4d3d9;
  border-radius: 6px;
  box-shadow: 0 18px 34px -31px rgba(38, 50, 56, 0.75);
  box-sizing: border-box;
  margin-top: 14px;
  padding: 16px;
  position: relative;
}

.provider-secondary-body-frame.is-tariff-section {
  background: #f5f7f8;
  border: 1px solid #c4d3d9;
  border-radius: 6px;
  box-shadow: 0 18px 34px -31px rgba(38, 50, 56, 0.75);
  margin-top: 14px;
  padding: 16px;
}

.provider-secondary-body-frame :deep(.card),
.provider-secondary-body-frame :deep(.card-panel) {
  border-color: #c4d3d9 !important;
  border-radius: 6px;
  box-shadow: 0 14px 24px -28px rgba(38, 50, 56, 0.72) !important;
}

.provider-secondary-body-frame :deep(.provider-tariff-card),
.provider-secondary-body-frame :deep(.provider-tariff-search-card),
.provider-secondary-body-frame :deep(.provider-tariff-row),
.provider-secondary-body-frame :deep(.provider-tariff-empty),
.provider-secondary-body-frame :deep(.profile-card),
.provider-secondary-body-frame :deep(.settings-summary),
.provider-secondary-body-frame :deep(.setting-card) {
  border-color: #c4d3d9 !important;
  box-shadow: 0 14px 24px -28px rgba(38, 50, 56, 0.72) !important;
}

.provider-secondary-body-frame :deep(.provider-tariff-row:hover),
.provider-secondary-body-frame :deep(.profile-card:hover),
.provider-secondary-body-frame :deep(.setting-card:hover) {
  border-color: #80cbc4 !important;
  box-shadow: 0 16px 26px -26px rgba(0, 105, 92, 0.62) !important;
}

.provider-secondary-body-frame :deep(.tariff-edit-action),
.provider-secondary-body-frame :deep(.provider-tariff-head),
.provider-secondary-body-frame :deep(.tariff-price),
.provider-secondary-body-frame :deep(.context-pill),
.provider-secondary-body-frame :deep(.provider-tariff-context span) {
  border-color: #d5e1e5 !important;
}

.pointable {
  cursor: default;
}

@media only screen and (max-width: 992px) {
  .provider-dossier-card,
  .provider-mode-strip,
  .provider-submode-strip {
    align-items: flex-start;
    flex-direction: column;
  }

  .provider-dossier-context {
    align-items: flex-start;
    flex-direction: row;
    flex-wrap: wrap;
  }

  .provider-mode-actions,
  .provider-submode-actions {
    justify-content: flex-start;
    max-width: 100%;
    min-width: 0;
    width: 100%;
  }

  .provider-submode-strip .provider-submode-actions {
    display: grid;
    gap: 8px;
    grid-template-columns: repeat(auto-fit, minmax(126px, 1fr));
    overflow-x: visible;
    padding-bottom: 0;
  }

  .provider-submode-strip .provider-submode-option {
    justify-content: center;
    min-width: 0;
    width: 100%;
  }
}

@media only screen and (min-width: 601px) and (max-width: 992px) {
  .provider-secondary-surface > .container {
    width: 92%;
  }

  .provider-dossier-card,
  .provider-mode-strip,
  .provider-submode-strip {
    padding: 22px;
  }

  .provider-secondary-body-frame,
  .provider-secondary-body-frame.is-tariff-section {
    padding: 18px;
  }

  .provider-mode-actions {
    flex-wrap: nowrap;
    overflow-x: auto;
    padding-bottom: 4px;
    -webkit-overflow-scrolling: touch;
  }

  .provider-mode-option {
    flex: 0 0 auto;
  }
}

@media only screen and (max-width: 600px) {
  .provider-super-card {
    align-items: flex-start;
  }

  .provider-dossier-card,
  .provider-secondary-body-frame {
    padding: 20px;
  }

  .provider-dossier-title h5 {
    font-size: 1.45rem;
  }

  .provider-dossier-title p {
    font-size: 0.94rem;
  }

  .provider-dossier-context {
    gap: 6px;
  }

  .provider-mode-actions {
    flex-wrap: nowrap;
    overflow-x: auto;
    padding-bottom: 2px;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
  }

  .provider-mode-actions::-webkit-scrollbar {
    display: none;
  }

  .provider-mode-option {
    flex: 0 0 auto;
  }

  .provider-secondary-body-frame,
  .provider-secondary-body-frame.is-tariff-section {
    padding: 12px;
  }

  .provider-secondary-body-frame :deep(.provider-tariff-card),
  .provider-secondary-body-frame :deep(.provider-tariff-search-card),
  .provider-secondary-body-frame :deep(.provider-tariff-row),
  .provider-secondary-body-frame :deep(.provider-tariff-empty),
  .provider-secondary-body-frame :deep(.profile-card),
  .provider-secondary-body-frame :deep(.settings-summary),
  .provider-secondary-body-frame :deep(.setting-card) {
    border-radius: 8px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .provider-super-card,
  .provider-dossier-card,
  .provider-mode-strip,
  .provider-submode-strip,
  .provider-secondary-body-frame,
  .provider-workbench :deep(.card),
  .provider-workbench :deep(.card-panel),
  .provider-workbench :deep(.provider-tariff-card),
  .provider-workbench :deep(.provider-tariff-search-card),
  .provider-workbench :deep(.provider-tariff-row),
  .provider-workbench :deep(.provider-tariff-empty),
  .provider-workbench :deep(.profile-card),
  .provider-workbench :deep(.settings-summary),
  .provider-workbench :deep(.setting-card) {
    animation: none;
  }
}
</style>
